import { useEffect, useRef, useState } from 'react';

/**
 * ── A NAME YOU CAN ACTUALLY TYPE INTO ───────────────────────────────────────
 *
 * WHAT WENT WRONG, reported by Vinnie on 1 September 2026 while walking #302:
 * *"I am unable to rename a Bug to a defect, because when I type into the text
 * field the screen jumps and there is a massive delay for letters to show up,
 * was not able to delete the B in Bug."*
 *
 * Both settings screens bound the box straight to the stored value and wrote on
 * every keystroke:
 *
 *     value={t.name}
 *     onChange={(e) => editType(t.id, { name: e.target.value })}
 *
 * So each letter was a write, a realtime announcement, a re-read and a
 * re-render — and the value in the box came back from the database rather than
 * from the person typing. Hence all three symptoms at once: the delay is the
 * round trip; the jump is the list re-sorting under a half-typed name; and
 * deleting the "B" in "Bug" is impossible because an empty name is refused by
 * `check (length(trim(name)) > 0)` and the old value is handed straight back.
 *
 * It is worse than slow. A rename typed at any speed writes a row per
 * character, so "Bug" to "Defect" leaves nine renames in the trail and nine
 * announcements to every other browser on the board.
 *
 * ⚠️ AND IT WAS IN BOTH SCREENS. TypeSettings was written from AreaSettings on
 * purpose and inherited it, so #269's areas have had the same fault since they
 * shipped. That is why this is one component rather than two repairs: the next
 * screen built from either of them gets the working one.
 *
 * ── WHAT IT DOES INSTEAD ────────────────────────────────────────────────────
 *
 * The person owns the text while they are typing, and the store hears about it
 * once, when they are done. `commit` runs on blur and on Enter; Escape puts it
 * back. Nothing is written when the name has not changed, and nothing is
 * written when it is blank — a blank is a half-finished edit, not an
 * instruction, and refusing it in the field means never sending the database
 * something it will reject anyway.
 *
 * IT STILL FOLLOWS THE STORED VALUE when that changes underneath — somebody
 * else renaming it on another machine — but only while this box is not being
 * typed in, because taking the words out from under somebody mid-sentence is
 * the fault this exists to fix, arriving from the other direction.
 */
export default function RenameField({
  value,
  onRename,
  label = 'Name',
  disabled = false,
}: {
  /** The stored name, as it is now. */
  value: string;
  /** Called once, with a trimmed non-empty name, only when it has changed. */
  onRename: (name: string) => void;
  label?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const typing = useRef(false);

  /* Follow the stored value, but never while somebody is in the box. */
  useEffect(() => {
    if (!typing.current) setDraft(value);
  }, [value]);

  const commit = () => {
    const wanted = draft.trim();
    /* A blank is an unfinished edit. Put the name back rather than sending a
       write the database is bound to refuse. */
    if (!wanted) {
      setDraft(value);
      return;
    }
    if (wanted === value.trim()) return;
    onRename(wanted);
  };

  return (
    <label className="area-field">
      {label}
      <input
        className="input"
        value={draft}
        disabled={disabled}
        onFocus={() => {
          typing.current = true;
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          typing.current = false;
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
          }
          if (e.key === 'Escape') {
            setDraft(value);
            typing.current = false;
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    </label>
  );
}
